import { ServiceItem } from '../types';
import { kopiereVorlage } from './bestandteile/bestandteilLogic';
import { kontingentFuerKopie } from './bestandteile/kontingentKopie';

/**
 * Bau einer NEUEN Projektposition aus einer Quellzeile.
 *
 * Zwei Quellen liefern solche Zeilen: der Gesamt-Katalog (service_catalog,
 * relational und typsicher) und eine benannte LV-Vorlage
 * (service_templates.items — ein jsonb-Array ohne jede Typgarantie, von Hand
 * oder per SQL befuellt). Beide muessen dieselbe Position ergeben, sonst
 * unterscheiden sich Projekte danach, ueber welchen Weg sie angelegt wurden.
 *
 * Bewusst rein und ohne DOM/Supabase: das Projekt hat kein jsdom, ein
 * Dialog-Test ist also nicht moeglich — nur diese Funktion ist testbar, und in
 * ihr sitzt die einzige Regel, die stillen Schaden anrichten kann (die
 * Vertragsmenge, siehe initialQuantityForSource).
 */

/**
 * Quellzeile. Absichtlich Partial: ein Vorlagen-Item kommt aus jsonb und kann
 * jedes Feld vermissen lassen — es als ServiceItem zu typisieren waere eine
 * Behauptung, die die Datenbank nicht deckt.
 */
export type ServiceItemSource = Partial<ServiceItem>;

/** Eine Menge ist nur brauchbar, wenn sie eine echte, positive Zahl ist. */
const isUsableQuantity = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Vorbelegte Vertragsmenge, wenn eine Position aus einer VORLAGE uebernommen
 * wird.
 *
 * Eine Vorlage ist ein bepreistes Leistungsverzeichnis: ihre Positionen tragen
 * die vereinbarten Mengen (520 Std., 832 Std., 5 psch.). Wuerde man sie wie
 * eine Katalogzeile mit 1 vorbelegen, stuende im Projekt ein LV, das mit dem
 * Vertrag nichts mehr zu tun hat — und niemand sieht es, weil eine 1 wie eine
 * bewusste Eingabe aussieht.
 *
 * Der Rueckfall auf 1 gilt nur, wenn die Vorlage selbst keine brauchbare Menge
 * nennt (fehlend, 0, NaN); eine Position mit Menge 0 waere sonst unsichtbar im
 * Projekt.
 */
export const initialQuantityForSource = (source: ServiceItemSource | null | undefined): number =>
    isUsableQuantity(source?.quantityContracted) ? (source as ServiceItem).quantityContracted : 1;

/**
 * Erzeugt aus Quellzeile + Menge eine frische Projektposition.
 *
 * `createId` wird injiziert (statt crypto.randomUUID direkt) — testbar ohne
 * Browser-Umgebung, gleiches Muster wie in checklistLogic.
 *
 * Nicht uebernommen werden bewusst:
 *  - die id der Quelle: sie gehoert dem Katalog bzw. der Vorlage. Uebernommen
 *    zeigten zwei Projekte auf dieselbe id, und save_project_children_tx
 *    schriebe sie gegenseitig um.
 *  - quantityBilled und dueDate: Abrechnungs- und Terminstand einer konkreten
 *    Position, in einer Vorlage bestenfalls ein Ueberbleibsel.
 *  - isDefault: eine reine Katalog-Eigenschaft ("im Wizard vorausgewaehlt"),
 *    die an einer Projektposition nirgends gelesen wird.
 */
export const createServiceItemFromSource = (
    source: ServiceItemSource,
    quantityContracted: number,
    createId: () => string,
): ServiceItem => {
    const item: ServiceItem = {
        id: createId(),
        lvPosition: source.lvPosition ?? '',
        title: source.title ?? '',
        unit: source.unit ?? '',
        unitPrice: Number(source.unitPrice) || 0,
        quantityContracted,
        quantityUsed: 0,
        status: 'open',
    };

    // Optionale Felder nur setzen, wenn die Quelle sie fuehrt. Ein
    // ausgeschriebenes `category: undefined` landete sonst als Schluessel im
    // JSON und damit in der Spaltenliste von save_project_children_tx.
    if (source.category != null) item.category = source.category;
    if (source.assignment != null) item.assignment = source.assignment;
    if (source.clientLvPosition != null) item.clientLvPosition = source.clientLvPosition;
    // Gleiche Schranke wie im Speicherpfad (cloudService: min_hours) — alles
    // andere ist keine vereinbarte Mindestmenge, sondern Rauschen.
    if (isUsableQuantity(source.minHours)) item.minHours = source.minHours;
    // PAKETPOSITION: die Bestandteile der Quelle als TIEFE, normalisierte
    // Kopie (Kernregel 8 — eine spaetere Katalog-/Vorlagenaenderung aendert
    // das Projekt nie). Ein Vorlagen-Item aus jsonb kann hier Unsinn tragen;
    // kopiereVorlage verwirft kaputte Elemente. Ob die Definition zur Menge
    // passt (E5: Menge 1), prueft der Dialog und sperrt das Speichern —
    // still weggelassen wuerde hier nichts.
    const bestandteile = kopiereVorlage(source.bestandteile);
    if (bestandteile) item.bestandteile = bestandteile;
    // Das Stundenkontingent reist NUR mit den Bestandteilen (S1,
    // docs/stundenkontingent-vertrag.md) und nur als gueltiger Wert — ein
    // Vorlagen-Item aus jsonb kann auch hier Unsinn tragen.
    Object.assign(item, kontingentFuerKopie(item, source.stundenKontingent));

    return item;
};
